const BASE = process.env.REACT_APP_API_URL || '';

// Broadcast when the server says the subscription is no longer active.
// Every API call funnels through req(), so this one hook covers the whole app.
//
// Deliberately narrow: it fires ONLY on a 402 carrying the exact
// SUBSCRIPTION_INACTIVE code, not on any 402 and not on other statuses. A
// broad trigger here would be dangerous — a spurious match would throw a
// working user into the lockout screen. It also does NOT reload the page:
// a reload on an error that persists would loop forever, and the React
// state transition is enough to swap in the lockout screen.
function notifySubscriptionInactive(detail) {
  try { window.dispatchEvent(new CustomEvent('subscription-inactive', { detail })); }
  catch { /* non-browser context — nothing to notify */ }
}

async function req(method, path, body, token) {
  const r = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type':'application/json', ...(token?{Authorization:`Bearer ${token}`}:{}) },
    ...(body ? { body: JSON.stringify(body) } : {})
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: r.statusText }));
    if (r.status === 402 && err.code === 'SUBSCRIPTION_INACTIVE') {
      notifySubscriptionInactive(err);
    }
    const e = new Error(err.error || `HTTP ${r.status}`);
    // Preserve the machine-readable parts so callers can branch on them
    // instead of string-matching the message.
    e.status = r.status;
    e.code   = err.code;
    throw e;
  }
  // PDF endpoint returns a blob
  const ct = r.headers.get('content-type') || '';
  if (ct.includes('application/pdf')) return r.blob();
  return r.json();
}

export const api = {
  withToken: (token) => ({
    getMe:               ()          => req('GET',  '/api/me',                null, token),
    updateMe:            (d)         => req('PUT',  '/api/me',                d,    token),
    markWelcomed:        ()          => req('POST', '/api/me/welcome',        {},   token),
    search:              (q,opts)    => req('POST', '/api/search',            {query:q,...opts}, token),
    suppliers:           ()          => req('GET',  '/api/suppliers',          null, token),
    productsByIds:       (ids)       => req('POST', '/api/products/by-ids',   {ids}, token),
    bundleSuggest:       (d)         => req('POST', '/api/bundle-suggest',    d,    token),
    getPresentations:    ()          => req('GET',  '/api/presentations',      null, token),
    createPresentation:  (d)         => req('POST', '/api/presentations',      d,    token),
    updatePresentation:  (id,d)      => req('PUT',  `/api/presentations/${id}`,d,    token),
    deletePresentation:  (id)        => req('DELETE',`/api/presentations/${id}`,null,token),
    downloadPDF:         (d)         => req('POST', '/api/presentations/pdf',  d,    token),
    getQuotes:           ()          => req('GET',  '/api/quotes',             null, token),
    updateQuote:         (id,status) => req('PUT',  `/api/quotes/${id}`,       {status}, token),
    estimateShipping:    (d)         => req('POST', '/api/shipping/estimate',  d,    token),
    createCheckout:      (data)      => req('POST', '/api/billing/checkout',   data, token),
    openPortal:          ()          => req('POST', '/api/billing/portal',     null, token),
    getBillingStatus:    ()          => req('GET',  '/api/billing/status',     null, token),
    // ── Team management ─────────────────────────────────────────────────────
    getTeam:             ()          => req('GET',  '/api/team',               null, token),
    inviteTeamMember:    (d)         => req('POST', '/api/team/invite',        d,    token),
    updateTeamMember:    (id,d)      => req('PUT',  `/api/team/${id}`,         d,    token),
    removeTeamMember:    (id)        => req('DELETE',`/api/team/${id}`,        null, token),
    acceptInvite:        (token2)    => req('POST', `/api/team/invite/${token2}/accept`, null, token),
    // ── Dashboard / reporting ───────────────────────────────────────────────
    getDashboardKpis:    ()          => req('GET',  '/api/dashboard/kpis',      null, token),
    getDashboardSeries:  (days)      => req('GET',  `/api/dashboard/timeseries${days ? `?days=${days}` : ''}`, null, token),
    getDashboardActivity:(limit)     => req('GET',  `/api/dashboard/activity${limit ? `?limit=${limit}` : ''}`, null, token),
    getRecentDecisions:  (limit)     => req('GET',  `/api/dashboard/recent-decisions${limit ? `?limit=${limit}` : ''}`, null, token),
    getClientHistory:    (email)     => req('GET',  `/api/clients/${encodeURIComponent(email)}/history`, null, token),
    // ── Clients (June 2026, Push C) ─────────────────────────────────────────
    // Lightweight customer records. ClientPicker autocomplete + Clients list page.
    listClients:         (q)         => req('GET',  `/api/clients${q ? `?q=${encodeURIComponent(q)}` : ''}`, null, token),
    getClient:           (id)        => req('GET',  `/api/clients/${id}`,      null, token),
    createClient:        (d)         => req('POST', '/api/clients',            d,    token),
    updateClient:        (id,d)      => req('PUT',  `/api/clients/${id}`,      d,    token),
    archiveClient:       (id)        => req('DELETE',`/api/clients/${id}`,     null, token),
    // Client addresses (Push C2)
    listClientAddresses:   (cid)         => req('GET',  `/api/clients/${cid}/addresses`,     null, token),
    createClientAddress:   (cid, d)      => req('POST', `/api/clients/${cid}/addresses`,     d,    token),
    updateClientAddress:   (cid, aid, d) => req('PUT',  `/api/clients/${cid}/addresses/${aid}`, d, token),
    archiveClientAddress:  (cid, aid)    => req('DELETE',`/api/clients/${cid}/addresses/${aid}`, null, token),
    // ── Vendors + decoration matrices (decorated-apparel quoting) ───────────
    // Vendors = PO-able decorators. Matrices = per-vendor decoration pricing tables.
    listVendors:             (q)       => req('GET',   `/api/vendors${q ? `?q=${encodeURIComponent(q)}` : ''}`, null, token),
    createVendor:            (d)       => req('POST',  '/api/vendors',                d,    token),
    updateVendor:            (id, d)   => req('PUT',   `/api/vendors/${id}`,          d,    token),
    archiveVendor:           (id)      => req('DELETE',`/api/vendors/${id}`,          null, token),
    listDecorationMatrices:  ()        => req('GET',   '/api/decoration-matrices',    null, token),
    createDecorationMatrix:  (d)       => req('POST',  '/api/decoration-matrices',    d,    token),
    updateDecorationMatrix:  (id, d)   => req('PUT',   `/api/decoration-matrices/${id}`, d, token),
    archiveDecorationMatrix: (id)      => req('DELETE',`/api/decoration-matrices/${id}`, null, token),
    // ── Supplier connections (per-distributor SanMar / S&S logins) ────────────
    listSupplierCredentials:  ()          => req('GET',    '/api/supplier-credentials',              null, token),
    saveSupplierCredential:   (d)         => req('PUT',    '/api/supplier-credentials',              d,    token),
    deleteSupplierCredential: (code)      => req('DELETE', `/api/supplier-credentials/${code}`,      null, token),
    testSupplierCredential:   (code, d)   => req('POST',   `/api/supplier-credentials/${code}/test`, d || {}, token),
    // Pull products from a connected supplier. slug is 'sanmar' | 'ssactivewear'.
    syncSupplierProducts:     (slug, ids) => req('POST',   `/api/${slug}/sync`, { productIds: ids, includeMedia: true }, token),
    previewSupplierProduct:   (slug, style) => req('GET',  `/api/${slug}/debug/raw/${encodeURIComponent(style)}`, null, token),
    // Full-catalog background sync (resumable) — enumerates + syncs the
    // ENTIRE supplier catalog in batches server-side. Poll status to show
    // progress; start() is safe to call again mid-run (it resumes).
    startFullCatalogSync:     (slug, batchSize) => req('POST', `/api/${slug}/sync-all/start`, { batchSize }, token),
    stopFullCatalogSync:      (slug)            => req('POST', `/api/${slug}/sync-all/stop`,  {},            token),
    getFullCatalogSyncStatus: (slug)            => req('GET',  `/api/${slug}/sync-all/status`, null,          token),
    getPricingBackfillStatus: (slug)            => req('GET',  `/api/${slug}/pricing-backfill/status`, null,   token),
    // Per-size Net (distributor cost) pricing for an apparel product.
    getSupplierPricing:       (slug, productId) => req('GET', `/api/${slug}/pricing/${encodeURIComponent(productId)}`, null, token),
    // ── Purchase Orders ──────────────────────────────────────────────────────
    getPOSettings:       ()          => req('GET',  '/api/account/po-settings', null, token),
    updatePOSettings:    (d)         => req('PUT',  '/api/account/po-settings', d,    token),
    previewPOs:          (d)         => req('POST', '/api/pos/preview',         d,    token),
    createPOs:           (d)         => req('POST', '/api/pos/create',          d,    token),
    getPOs:              (qs)        => req('GET',  `/api/pos${qs ? '?' + qs : ''}`, null, token),
    getPO:               (id)        => req('GET',  `/api/pos/${id}`,           null, token),
    updatePO:            (id,d)      => req('PUT',  `/api/pos/${id}`,           d,    token),
    deletePO:            (id)        => req('DELETE',`/api/pos/${id}`,          null, token),
    downloadPOPdf:       (id)        => req('GET',  `/api/pos/${id}/pdf`,       null, token),
    getPOEmailDraft:     (id)        => req('GET',  `/api/pos/${id}/email-draft`, null, token),
    emailPO:             (id,d)      => req('POST', `/api/pos/${id}/email`,     d||{}, token),
    // ── Invoices (Aug 2026) ──────────────────────────────────────────────────
    // Invoice collection from clients via Stripe
    getInvoices:         (status)    => req('GET',  `/api/invoices${status ? `?status=${status}` : ''}`, null, token),
    getInvoice:          (id)        => req('GET',  `/api/invoices/${id}`,      null, token),
    createInvoice:       (d)         => req('POST', '/api/invoices',            d,    token),
    getInvoiceEmailDraft:(id)        => req('GET',  `/api/invoices/${id}/email-draft`, null, token),
    emailInvoice:        (id,d)      => req('POST', `/api/invoices/${id}/email`, d||{}, token),
    // Stripe OAuth for distributor account connection
    getStripeConnectUrl: ()          => req('GET',  '/api/invoices/stripe/connect', null, token),
    disconnectStripe:    ()          => req('POST', '/api/invoices/stripe/disconnect', {}, token),
    // ── Beta gates ──────────────────────────────────────────────────────────
    acceptToS:           ()          => req('POST', '/api/me/accept-tos',       null, token),
    // ── Files (art uploads, mockups, PO PDFs — see lib/fileUpload.js for the
    //    full upload flow; these are the raw JSON endpoints only) ───────────
    presignFile:         (d)         => req('POST', '/api/files/presign',      d,    token),
    confirmFile:         (id)        => req('POST', `/api/files/${id}/confirm`, {},  token),
    getFile:             (id)        => req('GET',  `/api/files/${id}`,        null, token),
    getFileVersions:     (id)        => req('GET',  `/api/files/${id}/versions`, null, token),
    // Per-distributor email account (SendGrid, etc.) for client-decision notifications.
    getEmailCredentials: ()          => req('GET',  '/api/email-credentials',    null, token),
    saveEmailCredentials:(d)         => req('PUT',  '/api/email-credentials',    d,    token),
    deleteEmailCredentials: ()       => req('DELETE','/api/email-credentials',   null, token),
    testEmailCredentials:()          => req('POST', '/api/email-credentials/test', {}, token),
    listEmailSenders:    (d)         => req('POST', '/api/email-credentials/senders', d || {}, token),
    cancelSubscription:  (d)         => req('POST', '/api/billing/cancel',     d || {}, token),
    reactivateSubscription: ()       => req('POST', '/api/billing/reactivate', {},      token),
    setExtraSeats:       (n)         => req('POST', '/api/billing/seats',      { extra_seats: n }, token),
    getNewQuoteCount:    ()          => req('GET',  '/api/quotes/new-count',   null, token),
    getPaidInvoiceCount: ()          => req('GET',  '/api/invoices/paid-new-count', null, token),
    getNextInvoiceNumber: ()         => req('GET',  '/api/invoices/next-number', null, token),
    deleteFile:          (id)        => req('DELETE',`/api/files/${id}`,       null, token),
    getClientFiles:      (cid,kind)  => req('GET',  `/api/clients/${cid}/files${kind ? `?kind=${kind}` : ''}`, null, token),
  }),
  getPresentation:   (token) => req('GET',  `/api/p/${token}`),
  approvePresentation:(token)=> req('POST', `/api/p/${token}/approve`),
  requestChanges:    (token,msg)=>req('POST',`/api/p/${token}/changes`,{message:msg}),
  getStorefront:     (id)    => req('GET',  `/api/storefront/${id}`),
  submitQuote:       (id,d)  => req('POST', `/api/storefront/${id}/quote`, d),
  // Public invite-details endpoint — invitee may not be authenticated yet.
  getInvite:         (token) => req('GET',  `/api/team/invite/${token}`),
};
